import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, MapPin, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { adminStoresAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';
import { API_BASE_URL } from '../../config';
import GradientColorPicker from './GradientColorPicker';
import LogoPlacer from './LogoPlacer';
import StoreAccessPanel from './StoreAccessPanel';

/* ── Selector de color de texto (manual, con opción Automático) ── */
function TextColorPicker({ value, onChange }) {
  const presets = [
    { label: 'Blanco', val: '#FFFFFF' },
    { label: 'Negro',  val: '#0A0A0A' },
  ];
  const isAuto = !value || !value.trim();
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={() => onChange('')}
        className={`px-3 h-9 rounded-full text-xs font-medium border transition-all ${isAuto ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'}`}
      >
        Automático
      </button>
      {presets.map(p => {
        const active = !isAuto && value.toUpperCase() === p.val.toUpperCase();
        return (
          <button
            key={p.val}
            type="button"
            onClick={() => onChange(p.val)}
            className={`px-3 h-9 rounded-full text-xs font-medium border transition-all flex items-center gap-2 ${active ? 'border-black ring-2 ring-black/20' : 'border-gray-300 hover:border-gray-400'}`}
          >
            <span style={{ width: 14, height: 14, borderRadius: 4, background: p.val, border: '1px solid rgba(0,0,0,0.15)', display: 'inline-block' }} />
            {p.label}
          </button>
        );
      })}
      <label className="flex items-center gap-2 px-3 h-9 rounded-full text-xs font-medium border border-gray-300 hover:border-gray-400 cursor-pointer">
        <span style={{ width: 14, height: 14, borderRadius: 4, background: isAuto ? '#888' : value, border: '1px solid rgba(0,0,0,0.15)', display: 'inline-block' }} />
        Personalizado
        <input
          type="color"
          value={isAuto ? '#000000' : value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 0, height: 0, opacity: 0, position: 'absolute', pointerEvents: 'none' }}
        />
      </label>
    </div>
  );
}

const StoresManagement = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category: '',
    description: '',
    is_active: true,
    map_x: '',
    map_y: '',
    logo: '',
    color: '',
    text_color: '',
    logo_x: 50,
    logo_y: 66,
    logo_scale: 72,
    valid_until: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      const response = await adminStoresAPI.getAll();
      setStores(response.data || []);
    } catch (error) {
      toast({ title: "Error", description: "No se pudieron cargar las tiendas", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', slug: '', category: '', description: '', is_active: true, map_x: '', map_y: '', logo: '', color: '', text_color: '', logo_x: 50, logo_y: 66, logo_scale: 72, valid_until: '' });
    setLogoFile(null);
    setLogoPreview(null);
    setSelectedStore(null);
    setIsCreateMode(false);
  };

  const handleCreate = () => {
    resetForm();
    setIsCreateMode(true);
  };

  const handleEdit = (store) => {
    setSelectedStore(store);
    setIsCreateMode(false);
    const logoUrl = store.logoUrl || store.logo || store.image_url || '';
    setFormData({
      name: store.name || '',
      slug: store.slug || '',
      category: store.category || '',
      description: store.description || '',
      is_active: store.isActive ?? store.isParticipating ?? true,
      map_x: store.mapX?.toString() || store.mapPosition?.x?.replace('%', '') || '',
      map_y: store.mapY?.toString() || store.mapPosition?.y?.replace('%', '') || '',
      logo: logoUrl,
      color: store.color || '',
      text_color: store.text_color || '',
      logo_x: store.logo_x ?? 50,
      logo_y: store.logo_y ?? 66,
      logo_scale: store.logo_scale ?? 72,
      valid_until: store.validUntil || '',
    });
    setLogoFile(null);
    setLogoPreview(logoUrl ? `${API_BASE_URL}${logoUrl}` : null);
  };

  const handleDelete = async (storeId, storeName) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar la tienda "${storeName}"?`)) return;
    try {
      await adminStoresAPI.delete(storeId);
      setStores(stores.filter(s => s.id !== storeId));
      toast({ title: "Tienda eliminada", description: "La tienda ha sido eliminada correctamente" });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.error || error.response?.data?.message || "No se pudo eliminar la tienda", variant: "destructive" });
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.category) {
      toast({ title: "Error de validación", description: "Por favor completa los campos requeridos (Nombre, Categoría)", variant: "destructive" });
      return;
    }
    if (formData.map_x && (parseFloat(formData.map_x) < 0 || parseFloat(formData.map_x) > 100)) {
      toast({ title: "Error de validación", description: "La coordenada X debe estar entre 0 y 100", variant: "destructive" });
      return;
    }
    if (formData.map_y && (parseFloat(formData.map_y) < 0 || parseFloat(formData.map_y) > 100)) {
      toast({ title: "Error de validación", description: "La coordenada Y debe estar entre 0 y 100", variant: "destructive" });
      return;
    }

    try {
      setSaving(true);
      if (logoFile) {
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        if (formData.slug) formDataToSend.append('slug', formData.slug);
        formDataToSend.append('category', formData.category);
        if (formData.description) formDataToSend.append('description', formData.description);
        formDataToSend.append('is_active', formData.is_active ? '1' : '0');
        if (formData.map_x) formDataToSend.append('map_x', parseFloat(formData.map_x).toString());
        if (formData.map_y) formDataToSend.append('map_y', parseFloat(formData.map_y).toString());
        formDataToSend.append('logo', logoFile);
        if (formData.color) formDataToSend.append('color', formData.color);
        formDataToSend.append('text_color', formData.text_color || '');
        formDataToSend.append('logo_x', String(formData.logo_x ?? 50));
        formDataToSend.append('logo_y', String(formData.logo_y ?? 66));
        formDataToSend.append('logo_scale', String(formData.logo_scale ?? 72));

        let response;
        if (isCreateMode) {
          response = await adminStoresAPI.create(formDataToSend);
          setStores([...stores, response.data]);
          toast({ title: "Tienda creada", description: "La tienda ha sido creada correctamente" });
        } else {
          response = await adminStoresAPI.update(selectedStore.id, formDataToSend);
          setStores(stores.map(s => s.id === selectedStore.id ? { ...s, ...response.data } : s));
          toast({ title: "Cambios guardados", description: "La tienda ha sido actualizada correctamente" });
        }
        resetForm();
        return;
      }

      const payload = {
        name: formData.name,
        slug: formData.slug || undefined,
        category: formData.category,
        benefit: formData.benefit || undefined,
        description: formData.description || undefined,
        is_active: formData.is_active,
        map_x: formData.map_x ? parseFloat(formData.map_x) : undefined,
        map_y: formData.map_y ? parseFloat(formData.map_y) : undefined,
        color: formData.color || undefined,
        text_color: formData.text_color || '',
        logo_x: formData.logo_x ?? 50,
        logo_y: formData.logo_y ?? 66,
        logo_scale: formData.logo_scale ?? 72,
      };

      let response;
      if (isCreateMode) {
        response = await adminStoresAPI.create(payload);
        setStores([...stores, response.data]);
        toast({ title: "Tienda creada", description: "La tienda ha sido creada correctamente" });
      } else {
        response = await adminStoresAPI.update(selectedStore.id, payload);
        setStores(stores.map(s => s.id === selectedStore.id ? { ...s, ...response.data } : s));
        toast({ title: "Cambios guardados", description: "La tienda ha sido actualizada correctamente" });
      }
      resetForm();
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message ||
        error.response?.data?.errors?.[Object.keys(error.response.data.errors || {})[0]]?.[0] ||
        "No se pudieron guardar los cambios";
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const toggleStoreStatus = async (storeId, currentStatus) => {
    try {
      await adminStoresAPI.toggle(storeId, !currentStatus);
      setStores(stores.map(s =>
        s.id === storeId ? { ...s, isParticipating: !currentStatus, isActive: !currentStatus } : s
      ));
      toast({ title: "Estado actualizado", description: "El estado de la tienda ha sido modificado" });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.error || "No se pudo actualizar el estado", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <p className="text-gray-500">Cargando tiendas...</p>
      </div>
    );
  }

  return (
    <>
      <div className="p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Gestión de tiendas</h2>
              <p className="text-gray-500">Administra las tiendas participantes del outlet</p>
            </div>
            <Button
              onClick={handleCreate}
              className="bg-black text-white hover:bg-black/90 rounded-full px-6 py-3 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span>Nueva tienda</span>
            </Button>
          </div>

          <div className="space-y-3">
            {stores.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>No hay tiendas registradas</p>
                <p className="text-sm mt-2">Haz clic en "Nueva tienda" para crear una</p>
              </div>
            ) : (
              stores.map((store) => (
                <div
                  key={store.id}
                  className="bg-white border border-gray-200 rounded-3xl p-6 hover:border-gray-300 transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{store.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          (store.isActive ?? store.isParticipating)
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {(store.isActive ?? store.isParticipating) ? 'Activa' : 'Inactiva'}
                        </span>
                        {store.isFeatured && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                            Destacada
                          </span>
                        )}
                        {/* Una tienda activa sin PIN de caja no puede canjear
                            nada. Se avisa aquí porque de otro modo solo se
                            descubre cuando el cajero ya tiene la fila enfrente. */}
                        {(store.isActive ?? store.isParticipating) && !store.hasAccessPin && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            Caja sin PIN
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 text-sm mb-1">{store.category}</p>
                      {((store.mapX !== null && store.mapX !== undefined) ||
                        (store.mapY !== null && store.mapY !== undefined) ||
                        store.mapPosition) ? (
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                          <MapPin className="w-4 h-4" />
                          <span>
                            Posición: {store.mapX ?? store.mapPosition?.x ?? 'N/A'}, {store.mapY ?? store.mapPosition?.y ?? 'N/A'}
                          </span>
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={store.isActive ?? store.isParticipating}
                        onCheckedChange={() => toggleStoreStatus(store.id, store.isActive ?? store.isParticipating)}
                      />
                      <Button
                        onClick={() => handleEdit(store)}
                        variant="outline"
                        className="border-gray-200 text-gray-700 bg-transparent hover:bg-gray-100 rounded-full"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(store.id, store.name)}
                        variant="outline"
                        className="border-red-200 text-red-600 bg-transparent hover:bg-red-50 rounded-full"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!selectedStore || isCreateMode} onOpenChange={resetForm}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {isCreateMode ? 'Crear nueva tienda' : 'Editar tienda'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-900">Nombre *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12" placeholder="Ej: Nike" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-900">Slug (opcional)</Label>
                <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12" placeholder="Se genera automáticamente si se deja vacío" />
              </div>
            </div>

            {/* Acceso de caja: solo al editar. En el alta todavía no hay
                tienda a la que colgarle un enlace ni un PIN que entregar. */}
            {selectedStore && !isCreateMode && (
              <StoreAccessPanel
                store={selectedStore}
                onCambio={async () => {
                  await loadStores();
                  // El panel vive del objeto `selectedStore`; sin refrescarlo,
                  // seguiría diciendo «sin PIN» después de generarlo.
                  setSelectedStore((prev) => (prev ? { ...prev, hasAccessPin: true } : prev));
                }}
              />
            )}

            <div className="space-y-2">
              <Label className="text-gray-900">Categoría *</Label>
              <Input value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12" placeholder="Ej: Deportivo" />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-900">Descripción</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl min-h-[100px]" placeholder="Descripción de la tienda y sus beneficios..." />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-900">Coordenada X (%)</Label>
                <Input type="number" min="0" max="100" step="0.01" value={formData.map_x} onChange={(e) => setFormData({ ...formData, map_x: e.target.value })} className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12" placeholder="0-100" />
                <p className="text-xs text-gray-500">Usa valores de 0 a 100, donde 0,0 es la esquina superior izquierda del mapa.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-900">Coordenada Y (%)</Label>
                <Input type="number" min="0" max="100" step="0.01" value={formData.map_y} onChange={(e) => setFormData({ ...formData, map_y: e.target.value })} className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12" placeholder="0-100" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-900">Logo</Label>
              <div className="space-y-3">
                <Input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setLogoFile(file);
                      const reader = new FileReader();
                      reader.onloadend = () => setLogoPreview(reader.result);
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="bg-gray-50 border-gray-300 text-gray-900 rounded-2xl h-12 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-200 file:text-gray-700 hover:file:bg-gray-300"
                />
                {logoPreview && (
                  <div className="relative inline-block">
                    <img src={logoPreview} alt="Preview del logo" className="h-20 w-20 object-contain rounded-lg border border-gray-200" />
                    <button
                      type="button"
                      onClick={() => { setLogoFile(null); setLogoPreview(formData.logo ? `${API_BASE_URL}${formData.logo}` : null); }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {!logoPreview && formData.logo && (
                  <div className="relative inline-block">
                    <img src={`${API_BASE_URL}${formData.logo}`} alt="Logo actual" className="h-20 w-20 object-contain rounded-lg border border-gray-200" />
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">Formatos permitidos: JPG, JPEG, PNG, WEBP. Tamaño máximo: 3MB</p>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-900">Color de la tarjeta</Label>
              <GradientColorPicker
                value={formData.color || '#0A0A0A'}
                onChange={(color) => setFormData({ ...formData, color })}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-900">Color del texto</Label>
              <TextColorPicker
                value={formData.text_color}
                onChange={(text_color) => setFormData({ ...formData, text_color })}
              />
              <p className="text-gray-400 text-xs">
                "Automático" elige blanco o negro según el fondo. Elige un color fijo si lo prefieres.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-900">Posición y tamaño del logo</Label>
              <LogoPlacer
                logoUrl={logoPreview}
                color={formData.color || '#0A0A0A'}
                x={formData.logo_x ?? 50}
                y={formData.logo_y ?? 66}
                scale={formData.logo_scale ?? 72}
                onChange={({ x, y, scale }) => setFormData({ ...formData, logo_x: x, logo_y: y, logo_scale: scale })}
              />
            </div>

            {/* La vigencia de tienda se ocultó (Ricardo, 16-sep-2026), igual que la
                de los beneficios: una tienda se publica o se retira con su
                interruptor. La columna sigue en la base, sin que nada la escriba. */}

            <div className="flex items-center gap-2">
              <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
              <Label className="text-gray-900">Activa / Participando</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={resetForm} variant="outline" className="flex-1 border-gray-200 text-gray-700 bg-transparent hover:bg-gray-100 rounded-full py-6">
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-black text-white hover:bg-black/90 rounded-full py-6 transition-all hover:scale-105 active:scale-95 disabled:opacity-50">
                {saving ? 'Guardando...' : isCreateMode ? 'Crear tienda' : 'Guardar cambios'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster />
    </>
  );
};

export default StoresManagement;
